import Image from 'next/image';

import LogoSvg from '@/assets/medprev/logo.svg?url';
import { Appointment } from '@/types/appointments.types';
import { AppointmentStatus } from '@/types/enums/appointments.enum';
import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { medprevCnpj, visualEmailSac, visualPhonePatientSac } from '@/utils/consts/sac.consts';
import { dateFormatter } from '@/utils/formatters/date';
import { currencyFormatter, getPaymentMethodName } from '@/utils/formatters/currency';
import { formatPhoneNumber } from '@/utils/formatters/phonenumber';
import { formatDocumentNumber } from '@/utils/formatters/format-document-number';
import { addressFormatter } from '@/utils/formatters/format-address';
import { resolveAppointmentEntity } from '@/utils/formatters/appointment-entity';
import { clientSanitizeInput } from '@/utils/client-sanitize-input';

const GuideField = ({ label, children, className }:
{ label: string; children: React.ReactNode; className?: string }) => (
  <div className={`flex items-start overflow-hidden w-full ${className || ''}`}>
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
        {label}
      </Typography>
      <Typography variant={TypographyVariant.BODY_TEXT} as="div" className="w-full min-w-full">
        {children}
      </Typography>
    </div>
  </div>
);

const GuideInstructions = ({ content }: { content: string }) => (
  <div
    className="text-base text-gray-700 [&_p]:m-0"
    dangerouslySetInnerHTML={{ __html: clientSanitizeInput(content) ?? '' }}
  />
);

interface GuideProps {
  appointment: Appointment;
}

export function Guide({ appointment }: GuideProps) {
  const { facility } = appointment.partner;
  const isLaboratory = appointment.items.length > 1;
  const [firstItem] = appointment.items;

  const totalValue = appointment.items.reduce((sum, item) => sum + Number(item.billingPrice), 0);

  const entity = resolveAppointmentEntity(appointment);
  const professionalName = entity?.isProfessional ? entity.name : undefined;

  const renderDateTime = () => {
    if (appointment.scheduleType === ScheduleType.FLEX && !appointment.date) {
      return 'Você deve entrar em contato com o profissional ou parceiro por Telefone/WhatsApp para agendar.';
    }

    const formattedDate = dateFormatter(appointment.date, 'DD/MM/YYYY [às] HH:mm');

    if (
      (appointment.scheduleType === ScheduleType.ARRIVAL_ORDER
        || appointment.scheduleType === ScheduleType.LABORATORIAL)
      && appointment.end
    ) {
      const endTime = dateFormatter(appointment.end, 'HH:mm');
      return `${formattedDate} às ${endTime}, por ordem de chegada`;
    }

    return `${formattedDate} (horário de Brasília)`;
  };

  const formatPartnerPhones = (phones: string[]): string => {
    if (!phones || phones.length === 0) {
      return 'Não informado';
    }
    const uniquePhones = Array.from(new Set(phones));
    return uniquePhones.map((phone) => formatPhoneNumber(phone)).join(', ');
  };

  const shouldShowScheduledMessage = !appointment.hasFinished && appointment.status === AppointmentStatus.SCHEDULED;

  return (
    <div className="bg-white flex items-start justify-center px-11 py-10 w-full max-w-[794px] font-sans overflow-hidden print:w-[794px] print:py-8 print:min-h-[1123px]">
      <div className="flex flex-col gap-4 items-start w-full max-w-[706px] print:w-[706px] print:gap-3">
        <div className="flex gap-6 items-center w-full print:gap-5">
          <Image
            id="medprev-logo"
            src={LogoSvg}
            alt="Medprev Online"
            width={60}
            height={28}
            className="shrink-0 w-[60px] h-auto"
          />
          <div className="flex flex-col flex-1 min-w-0 items-start justify-end overflow-hidden">
            <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
              Medprev Online
            </Typography>
            <Typography variant={TypographyVariant.BODY_TEXT} className="w-full">
              CNPJ
              {' '}
              {medprevCnpj}
            </Typography>
          </div>
        </div>

        <div className="w-full h-0 border-t border-gray-300" />

        <Typography variant={TypographyVariant.MODAL_TITLE} className="text-center w-full">
          {`Guia ${appointment.referral}`}
        </Typography>

        <div className="flex flex-col items-start w-full">
          <div className="bg-gray-50 border border-gray-300 border-solid rounded-lg p-4 flex flex-col gap-4 w-full overflow-clip">
            {professionalName && (
              <GuideField label="Profissional">
                {professionalName}
              </GuideField>
            )}

            <GuideField label="Data e hora">
              {renderDateTime()}
            </GuideField>

            {!appointment.isTelehealth && (
              <GuideField label="Local">
                <div className="whitespace-pre-wrap">
                  <Typography variant={TypographyVariant.BODY_TEXT} className="leading-6 m-0">
                    {addressFormatter(facility, 'full')}
                  </Typography>
                  <Typography variant={TypographyVariant.BODY_TEXT} className="leading-6 m-0">
                    Contato:
                    {' '}
                    {formatPartnerPhones(facility.contacts.phones)}
                  </Typography>
                  <Typography variant={TypographyVariant.BODY_TEXT} className="leading-6 m-0">
                    {facility.contacts.email || '-'}
                  </Typography>
                </div>
              </GuideField>
            )}

            {appointment.partner && (
              <GuideField label="Parceiro">
                <Typography variant={TypographyVariant.BODY_TEXT}>
                  {appointment.partner.socialName || '-'}
                </Typography>
              </GuideField>
            )}

            <div className="w-full h-0 border-t border-gray-300" />

            {isLaboratory && (
              <div className="flex flex-col gap-4 w-full">
                <div className="bg-gray-100 border border-gray-400 border-solid flex items-center justify-center px-2 py-1 rounded relative w-full">
                  <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold whitespace-nowrap">
                    Procedimentos
                  </Typography>
                </div>

                {appointment.items.map((item, index) => (
                  <div key={item.id} className="flex flex-col gap-4 w-full">
                    <div className="flex gap-4 items-start w-full">
                      <div className="flex-1 overflow-clip">
                        <div className="flex flex-col">
                          <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
                            Procedimento
                          </Typography>
                          <Typography variant={TypographyVariant.BODY_TEXT}>
                            {item.procedure.name || '-'}
                          </Typography>
                        </div>
                      </div>
                      <div className="w-[120px] shrink-0 flex flex-col overflow-clip">
                        <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold w-full">
                          Valor
                        </Typography>
                        <Typography variant={TypographyVariant.BODY_TEXT} className="w-full">
                          {currencyFormatter(Number(item.billingPrice))}
                        </Typography>
                      </div>
                    </div>

                    {(item.procedure.notes || item.procedure.instructions) && (
                      <GuideField label="Instruções de preparo">
                        <GuideInstructions content={item.procedure.notes || item.procedure.instructions} />
                      </GuideField>
                    )}

                    {index < appointment.items.length - 1 && (
                      <div className="w-full h-0 border-t border-gray-300" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {!isLaboratory && firstItem && (
              <>
                <div className="flex gap-4 items-start w-full">
                  <GuideField label="Procedimento" className="flex-1">
                    <Typography variant={TypographyVariant.BODY_TEXT}>
                      {firstItem.procedure.name || '-'}
                    </Typography>
                  </GuideField>
                  <div className="flex flex-col overflow-hidden w-30 shrink-0">
                    <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold w-full">
                      Valor
                    </Typography>
                    <Typography variant={TypographyVariant.BODY_TEXT} className="w-full">
                      {currencyFormatter(Number(firstItem.billingPrice))}
                    </Typography>
                  </div>
                </div>

                {(firstItem.procedure.notes || firstItem.procedure.instructions) && (
                  <GuideField label="Instruções de preparo">
                    <GuideInstructions content={firstItem.procedure.notes || firstItem.procedure.instructions} />
                  </GuideField>
                )}
              </>
            )}

            <div className="w-full h-0 border-t border-gray-300" />

            <div className="flex items-start overflow-hidden w-full">
              <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
                  Atendimento para
                </Typography>
                <div className="w-full min-w-full whitespace-pre-wrap">
                  <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                    Nome completo:
                    {' '}
                    {appointment.patient.legalName || '-'}
                  </Typography>
                  <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                    Nome social:
                    {' '}
                    {appointment.patient.socialName || '-'}
                  </Typography>
                  <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                    CPF:
                    {' '}
                    {formatDocumentNumber(appointment.patient.documentNumber)}
                  </Typography>
                  <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                    E-mail:
                    {' '}
                    {appointment.customerContactEmail || '-'}
                  </Typography>
                  <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                    Data de nascimento:
                    {' '}
                    {dateFormatter(appointment.patient.birthday, 'date')}
                  </Typography>
                </div>
              </div>
            </div>

            <div className="w-full h-0 border-t border-gray-300" />

            <>
              <div className="flex items-start overflow-hidden w-full">
                <div className="flex flex-1 min-w-0 overflow-hidden">
                  <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                    <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
                      Detalhes da compra
                    </Typography>
                    <div className="w-full min-w-full">
                      <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                        Compra em
                        {' '}
                        {dateFormatter(appointment.createdAt, 'DD/MM/YYYY [às] HH:mm')}
                        {' '}
                        (
                        {getPaymentMethodName(appointment.payment.paymentMethod)}
                        )
                      </Typography>
                      <Typography variant={TypographyVariant.BODY_TEXT} className="m-0">
                        Titular do pagamento:
                        {' '}
                        {appointment.patient.legalName || '-'}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 w-full">
                <div className="flex items-start overflow-hidden w-full">
                  <div className="flex flex-1 min-w-0 overflow-hidden">
                    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                      <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
                        Valor total
                      </Typography>
                      <Typography variant={TypographyVariant.BODY_TEXT} className="w-full min-w-full">
                        {currencyFormatter(totalValue)}
                      </Typography>
                    </div>
                  </div>
                </div>

                <div className="flex items-start overflow-hidden w-30 shrink-0">
                  <div className="flex flex-1 min-w-0 overflow-hidden">
                    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                      <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold whitespace-nowrap">
                        Valor final
                      </Typography>
                      <Typography variant={TypographyVariant.BODY_TEXT} className="w-full min-w-full">
                        {currencyFormatter(appointment.payment.amount)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </>

            {shouldShowScheduledMessage && (
              <>
                <div className="w-full h-0 border-t border-gray-300" />

                <div className="flex flex-col gap-2 w-full">
                  <Typography variant={TypographyVariant.SMALL_BODY_TEXT} className="font-bold">
                    Atenção
                  </Typography>
                  <div className="space-y-2">
                    <>
                      <Typography variant={TypographyVariant.BODY_TEXT}>
                        A Medprev não é um plano de saúde e a rede de parceiros é independente. Informamos
                        {' '}
                        que, para fins de imposto de renda, a Nota Fiscal emitida pela Medprev não pode ser
                        {' '}
                        utilizada, uma vez que se refere a um serviço administrativo.
                      </Typography>
                      <Typography variant={TypographyVariant.BODY_TEXT}>
                        Para obter a nota fiscal correspondente à parte do valor do serviço prestado,
                        {' '}
                        recomendamos que você solicite diretamente ao local onde os procedimentos foram
                        {' '}
                        realizados.
                      </Typography>
                      <Typography variant={TypographyVariant.BODY_TEXT}>
                        Precisa de ajuda? Envie um e-mail para
                        {' '}
                        {visualEmailSac}
                        {' '}
                        ou entre em contato através do WhatsApp.
                      </Typography>
                    </>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="text-center w-full">
          <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold m-0">
            Equipe Medprev –
            {' '}
            {visualEmailSac}
          </Typography>
          <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold m-0">
            {visualPhonePatientSac}
            (WhatsApp)
          </Typography>
        </div>
      </div>
    </div>
  );
}
